import _ from 'lodash'
import { withCompInfo } from '@wix/editor-elements-integrations'
import {
	BaseRepeaterCss,
	ColumnsRepeaterCss,
	FluidColumnsRepeaterCss,
	RepeaterData,
	RepeaterDefinition,
	RepeaterProperties,
	ResponsiveRepeaterCss,
} from './Repeater'
import { MediaContainerDefinition } from '@wix/thunderbolt-components-native'
import { sizeToString } from '@wix/thunderbolt-commons'

const justifyContentOptions = {
	justify: 'space-between',
	right: 'end',
	left: 'start',
	center: 'center',
} as const

const getJustifyContent = (
	compProps: RepeaterProperties,
	compStyle: Record<string, string>
): BaseRepeaterCss['--justify-content'] => {
	return (compStyle.align as BaseRepeaterCss['--justify-content']) ?? justifyContentOptions[compProps.alignment]
}

const isJustifyAlignment = (compProps: RepeaterProperties, compStyle: Record<string, string>) =>
	getJustifyContent(compProps, compStyle) === 'space-between'

const castToInheritIfNeeded = (direction: 'rtl' | 'ltr' | undefined, parentDirection: 'rtl' | 'ltr') => {
	return direction === parentDirection || !direction ? 'inherit' : direction
}

const getDirection = (compProps: RepeaterProperties, compData: RepeaterData) => {
	const direction = compData.direction ?? castToInheritIfNeeded(compProps.direction, 'ltr') // TODO will need to pass real parent direction once available
	return direction === 'inherit' ? 'var(--wix-opt-in-direction)' : direction
}

const getGap = (compProps: RepeaterProperties, compStyle: Record<string, string>) => {
	const { gap } = compProps
	const isJustify = isJustifyAlignment(compProps, compStyle)
	const horizontalGap = isJustify ? 0 : (gap.horizontal || 0) / 2
	const verticalGap = (gap.vertical || 0) / 2

	return {
		horizontalGap,
		verticalGap,
	}
}

type GetCssMargin = {
	compProps: RepeaterProperties
	isFluid: boolean
	childDesign: MediaContainerDefinition['design']
	compStyle: Record<string, string>
}

const getCssMargin = ({ compProps, isFluid, childDesign, compStyle }: GetCssMargin) => {
	const { verticalGap, horizontalGap } = getGap(compProps, compStyle)
	const borderSize = _.get(childDesign, ['cssStyle', 'cssBorder', 'width', 'left', 'value'], 0)
	if (isFluid && borderSize !== 0) {
		const marginInlineStart = -borderSize - horizontalGap
		const marginInlineEnd = borderSize - horizontalGap
		const marginVertical = -borderSize - verticalGap
		return {
			'--margin-top': `${marginVertical}px`,
			'--margin-bottom': `${marginVertical}px`,
			'--margin-inline-start': `${marginInlineStart}px`,
			'--margin-inline-end': `${marginInlineEnd}px`,
		}
	} else {
		return {
			'--margin-top': `${-verticalGap}px`,
			'--margin-bottom': `${-verticalGap}px`,
			'--margin-inline-start': `${-horizontalGap}px`,
			'--margin-inline-end': `${-horizontalGap}px`,
		}
	}
}

type GetBaseNonResponsiveCss = {
	compProps: RepeaterProperties
	isFluid: boolean
	childDesign: MediaContainerDefinition['design']
	compData: RepeaterData
	compStyle: Record<string, string>
}

const getBaseNonResponsiveCss = ({
	compProps,
	compData,
	compStyle,
	isFluid,
	childDesign,
}: GetBaseNonResponsiveCss): BaseRepeaterCss => {
	const { verticalGap, horizontalGap } = getGap(compProps, compStyle)
	return {
		'--direction': getDirection(compProps, compData),
		'--justify-content': getJustifyContent(compProps, compStyle),
		'--item-margin': `${verticalGap}px ${horizontalGap}px`,
		...getCssMargin({ compProps, isFluid, childDesign, compStyle }),
	}
}

export const css = withCompInfo<
	ColumnsRepeaterCss | FluidColumnsRepeaterCss | ResponsiveRepeaterCss,
	RepeaterDefinition,
	{
		childData: { childDesign: MediaContainerDefinition['design'] }
	}
>()(
	[
		'compProps',
		'hasResponsiveLayout',
		'compLayout',
		'compSingleLayout',
		'isOneDocMigrated',
		'compData',
		'styleProperties',
	],
	(
		{ compProps, hasResponsiveLayout, compLayout, compSingleLayout, isOneDocMigrated, compData, styleProperties },
		{ childData }
	) => {
		if (hasResponsiveLayout) {
			return {}
		}
		const isFluid = isOneDocMigrated
			? compSingleLayout?.itemLayout?.type === 'MeshItemLayout' &&
			  compSingleLayout?.itemLayout.justifySelf === 'stretch'
			: !!compLayout.docked
		const baseNonResponsiveCss = getBaseNonResponsiveCss({
			compProps,
			isFluid,
			childDesign: childData.childDesign,
			compData,
			compStyle: styleProperties,
		})

		const dockedMargin = isOneDocMigrated
			? // @ts-ignore bad default value
			  sizeToString(_.get(compSingleLayout, ['itemLayout', 'margins', 'left'], 0))
			: `${_.get(compLayout, ['docked', 'left', 'px'], 0)}px`

		if (isFluid) {
			return {
				...baseNonResponsiveCss,
				'--docked-margin': dockedMargin,
			}
		} else {
			return baseNonResponsiveCss
		}
	}
)
